import React, { useContext, useEffect, useState } from 'react';
import { Input, Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { usePopupContext } from '../../PopupContext';
import Context from '../../context';
import { useGetToken } from '../../hooks/useGetToken';
import { fbDeleteForm, fbDuplicateForm, fbExtendForm, fbGetExport, fbGetForm, fbPostImport, fbPutForm, fbReleaseForm, FileType } from '../../services/service';
import { FbeForm, FbeFrontline, getLanguageDisplayName, } from '../../services/fbeTypes';
import { AxiosResponse } from 'axios';

interface ContextMenuProps {
  pk: string;
  sk: string;
  title: string;
  accessToken: string;
  hasExtension: boolean;
  anchorEl: null | HTMLElement;
  open: boolean;
  isLocalAdmin: boolean;
  isGlobalAdmin: boolean;
  active: boolean;
  languages: string[];
  onClose: () => void;
  onAction: (action: string) => void;
  frontline?: FbeFrontline;
}

export const fbImportSuccessMessage = 'Imported the data!';
const fbExportSuccessMessage = 'Exported the data!';
export const fbImportFailureMessage = 'Unable to import the data!';
const fbExportFailureMessage = 'Unable to export the data!';

const FormCardMenuComponent: React.FC<ContextMenuProps> = ({
  pk,
  sk,
  title,
  hasExtension,
  anchorEl,
  open,
  isLocalAdmin,
  isGlobalAdmin,
  active,
  onClose,
  languages,
  frontline,
}) => {
  const navigate = useNavigate();

  const deleteWarning = `Form deletion can not be undone. Do you want to delete the '${title}' form ? `;
  const deleteWarningWithExtension = `Form deletion can not be undone. This form has extensions. Do you want to delete the '${title}' form ?`;

  const isGlobalForm = pk === 'form_global';

  const showAllMenuItems =
    (isLocalAdmin && !isGlobalForm) || (isGlobalAdmin && isGlobalForm);
  const makeDisabled = !showAllMenuItems;

  const { updateIsLoading, setShowError, setShowSuccess, setMessage: setStatusMessage } = useContext(Context);

  const {
    setTrigger,
    setTitle,
    setMessage,
    setAction,
    setExecuteMethod,
    setMethod,
    setPk,
    setSk,
    executeMethod,
  } = usePopupContext();

  useEffect(() => {
    const execute = async () => {
      if (executeMethod === `${pk}-${sk}-handleDeleteForm`) {
        await handleDeleteForm();
        setExecuteMethod('');
      }
    };

    execute();
  }, [executeMethod]);

  const [getTokenFunction] = useGetToken();

  const handleReleaseForm = async (pk: string, sk: string) => {
    const accessToken = await getTokenFunction();

    updateIsLoading(true);

    await fbReleaseForm({ pk, sk } as FbeForm, accessToken)
      .then(() => {
        window.location.reload();
      })
      .catch((reason) => {
        updateIsLoading(false);
        setStatusMessage(reason);
        setShowError(true);
      })
      .finally(() => {
        updateIsLoading(false);
      });
  };

  const handleDeleteForm = async () => {
    const accessToken = await getTokenFunction();
    updateIsLoading(true);
    await fbDeleteForm({ pk, sk } as FbeForm, accessToken)
      .then(() => {
        window.location.reload();
        //      updateIsLoading(false);
      })
      .catch((reason) => {
        updateIsLoading(false);
        setStatusMessage(reason);
        setShowError(true);
      });
  };

  const handleActivationForm = async (pk: string, sk: string, activate: boolean) => {
    try{
      const accessToken = await getTokenFunction();

      updateIsLoading(true);
      const form = await fbGetForm( pk, sk , accessToken)
      if(form){
        await fbPutForm({...form, active: activate}, accessToken);
      }
      window.location.reload();
    } catch(error){
      updateIsLoading(false);
      setStatusMessage(`${error}`);
      setShowError(true);
    }
  };

  const fbDuplicateErrorMessage = 'Unable to duplicate form!';
  const fbDuplicateSuccessMessage = 'Duplicated form!';

  const handleDuplicateForm = async (pk: string, sk: string) => {
    try{
      const accessToken = await getTokenFunction();

      updateIsLoading(true);

      const referenceId = await fbDuplicateForm( pk, sk , accessToken);
      if(!referenceId){
        updateIsLoading(false);
        setStatusMessage(fbDuplicateErrorMessage);
        setShowError(true);
        return;
      } else {
        setStatusMessage(fbDuplicateSuccessMessage);
        setShowSuccess(true);
      }

      const parts = referenceId.referenceId.split('#');
      if(parts.length === 2){
        const duplicateForm = await fbGetForm(parts[0], parts[1], accessToken);
        updateIsLoading(false);
        navigate('/edit', { state: { formData: duplicateForm } });
      } else {
        updateIsLoading(false);
        setStatusMessage(fbDuplicateErrorMessage);
        setShowError(true);
      }
    } catch(error){
      updateIsLoading(false);
      setStatusMessage(`${error}`);
      setShowError(true);
    }
  };

  const fbExtendErrorMessage = 'Unable to extend global form!';
  const fbExtendSuccessMessage = 'Extended global form!';

  const handleExtendForm = async (pk: string, sk: string) => {
    try{
      const accessToken = await getTokenFunction();

      updateIsLoading(true);

      const referenceId = await fbExtendForm( pk, sk , accessToken);
      if(!referenceId){
        updateIsLoading(false);
        setStatusMessage(fbExtendErrorMessage);
        setShowError(true);
        return;
      } else {
        setStatusMessage(fbExtendSuccessMessage);
        setShowSuccess(true);
      }

      const parts = referenceId.referenceId.split('#');
      if(parts.length === 2){
        const extendForm = await fbGetForm(parts[0], parts[1], accessToken);
        updateIsLoading(false);
        navigate('/edit', { state: { formData: extendForm } });
      } else {
        updateIsLoading(false);
        setStatusMessage(fbExtendErrorMessage);
        setShowError(true);
      }
    } catch(error){
      updateIsLoading(false);
      setStatusMessage(`${error}`);
      setShowError(true);
    }
  };

  const [submenuAnchorElImport, setSubmenuAnchorElImport] = useState<null | HTMLElement>(null);
  const [submenuAnchorElExport, setSubmenuAnchorElExport] = useState<null | HTMLElement>(null);

  const handleExportMenuClick = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    setSubmenuAnchorElExport(event.currentTarget);
  };

  const handleSubMenuExportClose = () => {
    setSubmenuAnchorElExport(null);
  };

  const handleSubMenuImportClose = () => {
    setSubmenuAnchorElImport(null);
  };

  const handleExportOption = async (fileType: FileType, language?: string) => {
    const fileName = fileType === FileType.FORM ? `form_${pk}_${sk}.json` : `localization_${pk}_${sk}_${language}.tsv`

    closeSubMenus();
    try{
      const accessToken = await getTokenFunction();

      updateIsLoading(true);

      const response = await fbGetExport(fileType, `${pk}%23${sk}%23${language}`,accessToken);

      if(response && response.data){
        // Create a blob and download
        const blob = new Blob([fileType === FileType.FORM ? JSON.stringify(response.data) : response.data], { type: response.headers['content-type'] });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(link.href);
        setStatusMessage(fbExportSuccessMessage);
        setShowSuccess(true);
      } else {
        setStatusMessage(fbExportFailureMessage);
        setShowError(true);
      }
      updateIsLoading(false);
    } catch(error){
      updateIsLoading(false);
      setStatusMessage(`${error}`);
      setShowError(true);
    }
  };

  const [fileType, setFileType] = useState<FileType>(FileType.FORM);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');

  const handleImportMenuClick = (event: React.MouseEvent<HTMLLIElement>) => {
    event.stopPropagation();
    setSubmenuAnchorElImport(event.currentTarget);
  };

  const handleImportOption = (fileType: FileType, language?: string) => {
    const fileInput = document.getElementById(`${pk}_${sk}_file_input`) as HTMLInputElement | null;
    fileInput?.click();
    setSelectedLanguage(language || '');
    setFileType(fileType);
    closeSubMenus();
  }

  const closeSubMenus = (): void => {
    handleSubmenuLocalizationClose();
    handleSubmenuQuestionsClose()
    handleSubMenuExportClose();
    handleSubMenuImportClose();
  }

  const handleImportFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFile = event.target.files ? event.target.files[0] : '';

    closeSubMenus();

    if (selectedFile) {
      updateIsLoading(true);
      const accessToken = await getTokenFunction();

      const formData = new FormData();
      formData.append('file', selectedFile);

      try {
        const response = await fbPostImport(
          fileType,
          `${pk}%23${sk}%23${selectedLanguage}`,
          formData,
          accessToken);
        if(response && response.status === 200){
          setStatusMessage(fbImportSuccessMessage);
          setShowSuccess(true);
        } else {
          const moreInfo = (fileType ===FileType.QUESTIONS) ? response?.data : ''
          setStatusMessage(`${fbImportFailureMessage} ${moreInfo}`);
          setShowError(true);
        }
      } catch(error){
        const moreInfo = (fileType ===FileType.QUESTIONS) ? (error as AxiosResponse)?.data : ''
        setStatusMessage(`${fbImportFailureMessage} ${moreInfo}`);
        setShowError(true);
      }
      updateIsLoading(false);
    }
  }

  const [submenuAnchorLocalizationEl, setSubmenuAnchorLocalizationEl] = useState<null | HTMLElement>(null);

  const handleSubmenuLocalizationOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setSubmenuAnchorLocalizationEl(event.currentTarget);
  };

  const handleSubmenuLocalizationClose = () => {
    setSubmenuAnchorLocalizationEl(null);
    setSubmenuAnchorElImport(null);
  };

  const handleSubmenuQuestionsClose = () => {
    setSubmenuAnchorElImport(null);
  };

  const onCloseHandler = () : void => {
    closeSubMenus();
    onClose();
  }

  return (
    <>
    <Menu
      data-testid="ContextMenu"
      anchorEl={anchorEl}
      open={open}
      onClose={onCloseHandler}
      onClick={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <MenuItem
        disabled={false}
        data-testid="Edit"
        onClick={() => navigate('/edit', { state: { formData: { pk, sk } } })}
        aria-label="Edit form"
      >
        {makeDisabled ? 'View' : 'Edit' }
      </MenuItem>
      <MenuItem
        disabled={makeDisabled}
        data-testid="Release"
        onClick={() => handleReleaseForm(pk, sk)}
        aria-label="Release form"
      >
        Release
      </MenuItem>
      <MenuItem
        disabled={makeDisabled}
        data-testid="Deactivate"
        onClick={() => handleActivationForm(pk, sk, !active)}
        aria-label="Deactivate form"
      >
        {active ? 'Deactivate' : 'Activate'}
      </MenuItem>
      <MenuItem
        disabled={false}
        data-testid="Duplicate"
        onClick={() => handleDuplicateForm(pk, sk)}
        aria-label="Duplicate form"
      >
        Duplicate
      </MenuItem>
      <MenuItem
        disabled={hasExtension || (isGlobalForm && isGlobalAdmin) || (!isGlobalForm && isLocalAdmin)}
        data-testid="Extend"
        onClick={() => handleExtendForm(pk, sk)}
        aria-label="Extend form"
      >
        Extend
      </MenuItem>

      <MenuItem
        disabled={makeDisabled}
        data-testid="Export"
        onClick={handleExportMenuClick}
        aria-label="Export"
      >
        Export
      </MenuItem>

      <Menu
        anchorEl={submenuAnchorElExport}
        open={Boolean(submenuAnchorElExport)}
        onClose={handleSubMenuExportClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={() => handleExportOption(FileType.FORM,'')} data-testid="export-option-form">
          Form
        </MenuItem>
        { languages.map(language => (
            <MenuItem onClick={() => handleExportOption(FileType.LOCALIZATION,`${language}`)} data-testid={`export-option-${language}`}>
            Localization: {getLanguageDisplayName(language, frontline?.settings.languageDisplayNames || {})}
            </MenuItem>
      ))}
      </Menu>

      <MenuItem
        disabled={makeDisabled}
        data-testid="Import"
        onClick={handleImportMenuClick}
        aria-label="Import"
      >
        Import
      </MenuItem>

      <Menu
        anchorEl={submenuAnchorElImport}
        open={Boolean(submenuAnchorElImport)}
        onClose={onCloseHandler}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={() => handleImportOption(FileType.FORM,'')} data-testid="import-option-form">
          Form
        </MenuItem>

        <MenuItem onClick={handleSubmenuLocalizationOpen}>Localization</MenuItem>
          <Menu
            anchorEl={submenuAnchorLocalizationEl}
            open={Boolean(submenuAnchorLocalizationEl)}
            onClose={handleSubmenuLocalizationClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
          { languages.map(language => (
              <MenuItem onClick={() => handleImportOption(FileType.LOCALIZATION,`${language}`)} data-testid={`import-option-${language}`}>
             {getLanguageDisplayName(language, frontline?.settings.languageDisplayNames || {})}
              </MenuItem>
          ))}
        </Menu>

        <MenuItem onClick={() => handleImportOption(FileType.QUESTIONS,selectedLanguage)} data-testid="import-option-form">
          Questions
        </MenuItem>

      </Menu>

      <MenuItem
        disabled={makeDisabled}
        data-testid="Delete"
        onClick={() => {
            setTitle('Delete form');
            setMessage(
              hasExtension
                ? `${deleteWarningWithExtension}`
                : `${deleteWarning}`
            );
            setAction('Delete');
            setExecuteMethod('');
            setMethod('handleDeleteForm');
            setPk(pk);
            setSk(sk);
            setTrigger(true);
        }}
        aria-label="Delete form"
      >
        Delete
      </MenuItem>
    </Menu>
      <Input
      id={`${pk}_${sk}_file_input`}
      type="file"
      onChange={handleImportFileChange}
      disabled={false}
      style={{ display: 'none' }}
    />
    </>
  );
};

export default FormCardMenuComponent;
